import React from 'react';

import classNames from 'classnames';
import Typography from '../Typography/index.js';

type LoaderColor = 'primary' | 'gray' | 'white' | 'black';
type LoaderVariant = 'bar' | 'dot';

export interface LoaderProps {
  /**
   * @ignore
   * Classes applied to the root element.
   */
  className?: string;

  /**
   * The color of the dot pulse or the moving bar (depending on variant)
   * @default 'primary'
   */
  color?: LoaderColor;

  /**
   * The label for the Loader.
   * Only applicable to full loaders
   */
  label?: string;

  /**
   * The sub-label of the Loader.
   * Only applicable to full loaders.
   *
   * Pass this if you want to show extra contextual information to the user.
   */
  subLabel?: string;

  /**
   * The variant of loader to use for the loader.
   * @default "bar"
   */
  variant?: LoaderVariant;
}

/**
 * Loaders are used to display states that need time to be able to be presented
 * in the interface, such as showing a long list, or performing an action such
 * as uploading something. We have 2 types, outlined below.
 *
 * This loader is used when you are replacing a section on a page that contains
 * multiple elements. Examples include things like lists, full cards, & modals.
 * If you are showing an individual item being loaded, use the 3-dot format
 * outlined below.
 *
 * This loader is used when you are showing an individual item that is being
 * loaded. It’s used when you have to show how one item is loading against
 * multiple similar items. Examples include a single line-item in a list.
 *
 * @see https://www.figma.com/file/LdhxwbVqKRRLpyK5ekr4Q8/Component-Library?node-id=2442%3A5656&t=TJpvgjcJzv27NfVe-0
 */
const Loader = React.forwardRef(
  (
    {
      className,
      color = 'primary',
      label,
      subLabel,
      variant = 'bar',
    }: LoaderProps,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    if (variant === 'dot') {
      return (
        <div
          className={classNames('dot-pulse', `color-${color}`, className)}
          ref={ref}
        />
      );
    }

    return (
      <div className={classNames('loading-bar-container', className)} ref={ref}>
        <div className={'loading-bar-label-container'}>
          <Typography variant="title-large" bold>
            {label}
          </Typography>
          {subLabel && (
            <Typography variant="paragraph-large">{subLabel}</Typography>
          )}
        </div>
        <div className="loading-bar">
          <div className={classNames('loading-moving-bar', `color-${color}`)} />
        </div>
      </div>
    );
  },
);

Loader.displayName = 'Loader';

export default Loader;
